'use client';

import { Icon } from '@/components/Icon';
import type { LocationDto } from '@/lib/api';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolveCenterName } from '@/lib/location/centerName';
import { locationMapsUrl } from '@/lib/location/mapsUrl';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';
import type { CtaAction } from '@/types/ctaAction';

/**
 * A `widgets.cta-action` of type `location`: the page's Standort address as a
 * plain primary-coloured link with a pin, street on the first row and PLZ +
 * city on the second, opening Google Maps (the legacy Standort-Jobs hero card,
 * spec 10.03.03.19 § AC 2). Label and link come from the Standort, so the
 * action's own `label` and `link` are ignored. Renders nothing on a page
 * without a Standort or for a Standort without a street.
 */
export function LocationCtaLink({ action }: { action: CtaAction }) {
  const location = useOptionalRenderContext()?.entities.location as
    LocationDto | undefined;
  const street = location?.street?.trim();
  if (!location || !street) {
    return null;
  }
  const zipCode = location.zipCode ?? '';
  const cityName = location.cityName ?? '';
  const centerName = resolveCenterName(location);
  const href = locationMapsUrl({
    name: centerName.includes('Schülerhilfe')
      ? centerName
      : `Schülerhilfe ${centerName}`,
    street,
    zipCode,
    cityName,
  });
  const address = `${street}, ${zipCode} ${cityName}`.trim();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      // Weight and hover follow the primary button (`<Button>`): medium text,
      // the brand blue at 90% on hover — no underline. `inline-block` so the
      // two rows take the card's own text alignment (centred on the centred
      // card, as on the legacy page), with the pin inline before the street.
      className="text-primary hover:text-primary/90 inline-block text-base font-medium transition-colors md:text-lg"
      onClick={() => pushCmsEventTracking(action.tracking, { label: address })}
    >
      <Icon
        src="/icons/map-pin.svg"
        size={20}
        className="mr-1.5 mb-0.5 inline-block align-text-bottom"
      />
      {street}
      <br />
      {zipCode} {cityName}
    </a>
  );
}
